import { Request, Response } from '@luxuryescapes/contract-svc-tour';

export type Supplier = Response.Supplier;

export type CreateSupplierPayload = Request.PostSupplierBody;

export type ServiceKind = Request.ServiceKind;

export const SERVICE_KINDS: ReadonlyArray<ServiceKind> = ['Tour', 'Accommodation', 'OptionalExperience', 'Flight'];

export type ServiceCostPriceType = Request.ServiceCostPriceType;

export type ServiceCostPriceScope = Request.ServiceCostPriceScope;

export const SERVICE_COST_PRICE_TYPES: ReadonlyArray<ServiceCostPriceType> = ['SlidingScale', 'Fixed'];

export const SERVICE_COST_PRICE_SCOPES: ReadonlyArray<ServiceCostPriceScope> = ['Global', 'Departure'];

export const SERVICE_COST_PRICE_TYPE_LABELS: Record<ServiceCostPriceType, string> = {
  SlidingScale: 'Sliding scale',
  Fixed: 'Fixed',
};

export const SERVICE_COST_PRICE_SCOPE_LABELS: Record<ServiceCostPriceScope, string> = {
  Global: 'Whole tour',
  Departure: 'Per departure',
};

const NOT_COSTED = '—';

export function costPriceTypeLabel(type?: ServiceCostPriceType): string {
  return type ? SERVICE_COST_PRICE_TYPE_LABELS[type] : NOT_COSTED;
}

export function costPriceScopeLabel(scope?: ServiceCostPriceScope): string {
  return scope ? SERVICE_COST_PRICE_SCOPE_LABELS[scope] : NOT_COSTED;
}

export type CreateServicePayload = Request.PostServiceBody;

export type CostPriceKind = Request.CostPriceKind;

const COST_PRICE_KIND_BY_TYPE_AND_SCOPE: Record<`${ServiceCostPriceType}-${ServiceCostPriceScope}`, CostPriceKind> = {
  'SlidingScale-Global': 'SlidingScale',
  'SlidingScale-Departure': 'SlidingScaleByDeparture',
  'Fixed-Global': 'Fixed',
  'Fixed-Departure': 'FixedByDeparture',
};

/**
 * Mirrors svc-tour: kind comes from the service, not the row. Absent fields
 * fall back to the same column defaults svc-tour stores, so a service served
 * without them behaves as the one it would have created.
 */
export function costPriceKindFromService(service: {
  costPriceType?: ServiceCostPriceType;
  costPriceScope?: ServiceCostPriceScope;
}): CostPriceKind {
  const type = service.costPriceType ?? 'SlidingScale';
  const scope = service.costPriceScope ?? 'Global';

  return COST_PRICE_KIND_BY_TYPE_AND_SCOPE[`${type}-${scope}`];
}

/** Writing a kind the service does not declare is accepted, then never read back. */
export function servicePricing(service: {
  costPriceType?: ServiceCostPriceType;
  costPriceScope?: ServiceCostPriceScope;
}) {
  const kind = costPriceKindFromService(service);

  return {
    kind,
    canUploadSlidingScale: kind === 'SlidingScale',
    canSetFixedPrice: kind === 'Fixed' || kind === 'FixedByDeparture',
    // The only kind with no way to price it, so it is named rather than
    // inferred from the two flags being false.
    hasNoPricingPath: kind === 'SlidingScaleByDeparture',
  };
}
